import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiMapPin } from "react-icons/fi";

import MultiTable from "../../Common/MUITable";
import { getAllComplaintCircles } from "../../Apis/complaintApi";
import {
  getAllRfidCategories,
  getAssetCategoryByRfidType,
  getRfidTypes,
} from "../../Apis/rfidApi";
import { RfidMapContent } from "../Rfid/RfidMapView";
import useGoogleMapsLoader from "../../../Hooks/useGoogleMapsLoader";
import HandHeldReaderCollectionFilter from "./HandHeldReaderCollectionFilter";
import { ExportHandHeldReaderCollection } from "./ExportHandHeldReaderCollection";
import {
  getDefaultHandHeldFilters,
  useHandHeldReaderCollectionStore,
} from "./store/useHandHeldReaderCollectionStore";

const EXPORT_COLUMNS = [
  { label: "Asset Code", field: "assetCode" },
  { label: "Asset Type", field: "assetTypeName" },
  { label: "Asset Category", field: "assetCategoryName" },
  { label: "IMEI/SL/MFG.", field: "imeiSlMfgNo" },
  { label: "Ward", field: "wardNo" },
  { label: "Zone", field: "zoneNo" },
  { label: "Scanned By Reader", field: "scannedByReader" },
  { label: "Scanned On", field: "scannedOn" },
];

const HandHeldReaderCollection = () => {
  const [wards, setWards] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetCategories, setAssetCategories] = useState([]);
  const [viewMapRow, setViewMapRow] = useState(null);

  useGoogleMapsLoader();

  const {
    reportRows,
    isLoading,
    fetchHandHeldReaderCollection,
    resetFilters,
    filters,
    pagination,
    setReportPage,
    setReportPageSize,
  } = useHandHeldReaderCollectionStore();

  useEffect(() => {
    const init = async () => {
      try {
        const [circleResponse, typeResponse, categoryResponse] =
          await Promise.all([
            getAllComplaintCircles(),
            getRfidTypes(),
            getAllRfidCategories(),
          ]);

        setWards(
          (circleResponse || []).filter((circle) => circle.isActive !== false)
        );
        setAssetTypes(typeResponse || []);
        setAssetCategories(categoryResponse || []);
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }

      fetchHandHeldReaderCollection({ pageNumber: 1 });
    };

    init();
  }, []);

  const loadCategoriesByType = async (assetTypeId) => {
    if (!assetTypeId) {
      try {
        const categories = await getAllRfidCategories();
        setAssetCategories(categories || []);
      } catch (error) {
        console.error("Error fetching asset categories:", error);
        setAssetCategories([]);
      }
      return;
    }

    try {
      const response = await getAssetCategoryByRfidType(assetTypeId);
      setAssetCategories(response || []);
    } catch (error) {
      console.error("Error fetching categories by asset type:", error);
      setAssetCategories([]);
    }
  };

  const handleViewOnMap = (row) => {
    if (!row) return;
    const lat = Number(row?.latitude ?? row?.Latitude);
    const lng = Number(row?.longitude ?? row?.Longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    setViewMapRow(row);
  };

  const closeViewMapModal = () => setViewMapRow(null);

  const applyReportFilters = (values) => {
    fetchHandHeldReaderCollection({
      ...values,
      pageNumber: 1,
    });
  };

  const tableColumns = [
    {
      label: "View On Map",
      field: "viewOnMap",
      width: "120px",
      render: (row) => {
        const lat = Number(row?.latitude ?? row?.Latitude);
        const lng = Number(row?.longitude ?? row?.Longitude);
        const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng);

        return (
          <button
            type="button"
            disabled={!hasCoords}
            onClick={() => handleViewOnMap(row)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              border: "none",
              background: "transparent",
              cursor: hasCoords ? "pointer" : "not-allowed",
              padding: 0,
            }}
          >
            <FiMapPin size={14} color={hasCoords ? "#EC1117" : "#CBD5E1"} />
            <span style={{ color: hasCoords ? "rgb(72, 55, 55)" : "#CBD5E1" }}>
              View
            </span>
          </button>
        );
      },
    },
    { label: "Asset Code", field: "assetCode", width: "140px" },
    { label: "Asset Type", field: "assetTypeName", width: "180px" },
    { label: "Asset Category", field: "assetCategoryName", width: "160px" },
    { label: "IMEI/SL/MFG.", field: "imeiSlMfgNo", width: "150px" },
    { label: "Ward", field: "wardNo", width: "100px" },
    { label: "Zone", field: "zoneNo", width: "100px" },
    { label: "Scanned By Reader", field: "scannedByReader", width: "160px" },
    { label: "Scanned On", field: "scannedOn", width: "160px" },
  ];

  return (
    <>
      <div className="content text-start">
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mt-3">
            <div>
              <h5 className="mb-1 fw-semibold">
                Hand Held Reader Collection
              </h5>
             
            </div>
            <ExportHandHeldReaderCollection
              reportData={reportRows}
              columns={EXPORT_COLUMNS}
            />
          </div>

          <div className="card shadow mt-3">
            <div className="card-body">
              <HandHeldReaderCollectionFilter
                initialValues={{
                  circleId: filters.circleId || "",
                  assetCategoryId: filters.assetCategoryId
                    ? String(filters.assetCategoryId)
                    : "",
                  assetTypeId: filters.assetTypeId
                    ? String(filters.assetTypeId)
                    : "",
                  fromDate:
                    filters.fromDate || getDefaultHandHeldFilters().fromDate,
                  toDate: filters.toDate || getDefaultHandHeldFilters().toDate,
                }}
                wards={wards}
                assetTypes={assetTypes}
                assetCategories={assetCategories}
                onAssetTypeChange={loadCategoriesByType}
                onSearch={applyReportFilters}
                onReset={() => {
                  loadCategoriesByType("");
                  resetFilters();
                }}
              />

              <div>
                <MultiTable
                  needSRNumber
                  columns={tableColumns}
                  rows={reportRows}
                  loading={isLoading}
                  tableHeight={500}
                  serverPagination
                  totalCount={pagination.totalRecords}
                  page={Math.max(0, (pagination.pageNumber || 1) - 1)}
                  rowsPerPage={pagination.pageSize || 10}
                  onPageChange={(_event, newPage) => setReportPage(newPage + 1)}
                  onRowsPerPageChange={(event) =>
                    setReportPageSize(parseInt(event.target.value, 10))
                  }
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={!!viewMapRow}
        onHide={closeViewMapModal}
        size="xl"
        centered
        aria-labelledby="handheld-reader-view-map-title"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="handheld-reader-view-map-title">
            View on Map
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 16 }}>
          {viewMapRow && (
            <RfidMapContent
              key={
                viewMapRow.assetCode ??
                viewMapRow.imeiSlMfgNo ??
                viewMapRow.scannedOn
              }
              row={viewMapRow}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default HandHeldReaderCollection;
